import { atom } from 'jotai';
import type { AspectRatio, Mode, Quality } from '../constants/options';

export type JobStatus = 'idle' | 'generating' | 'ready' | 'error';
export type ActiveTab = 'main' | 'adjustments';

export interface ImageRef {
  id: string;
  url: string;
}

export type CropAspectOption = 'source' | AspectRatio;

export interface StyleConfig {
  estateId: string | null;
  projectId: string | null;
  mode: Mode;
  images: ImageRef[];
  startImageId?: string;
  endImageId?: string;
  aspectRatio: AspectRatio;
  cropToAspectRatio: CropAspectOption;
  quality: Quality;
  durationSec: number;
}

export interface AdjustmentsConfig {
  voiceover: { enabled: boolean; text: string };
  subtitles: { enabled: boolean };
  speakerView: { enabled: boolean };
  music: { enabled: boolean };
}

export interface RuntimeState {
  status: JobStatus;
  previewUrl?: string;
  errorMessage?: string;
  taskId?: string;
  lastCompletedTaskId?: string;
  videoName?: string;
}

export interface UiState {
  activeTab: ActiveTab;
  selectedImageId?: string;
}

export interface ImageToVideoState {
  style: StyleConfig;
  adjustments: AdjustmentsConfig;
  runtime: RuntimeState;
  ui: UiState;
}

export const initialImageToVideoState: ImageToVideoState = {
  style: {
    estateId: null,
    projectId: null,
    mode: 'walkthrough',
    images: [],
    aspectRatio: '16:9',
    cropToAspectRatio: 'source',
    quality: '720p',
    durationSec: 3,
  },
  adjustments: {
    voiceover: { enabled: false, text: '' },
    subtitles: { enabled: false },
    speakerView: { enabled: false },
    music: { enabled: false },
  },
  runtime: { status: 'idle', videoName: 'Video.mp4' },
  ui: { activeTab: 'main' },
};

export const imageToVideoAtom = atom<ImageToVideoState>(initialImageToVideoState);

// Selected object + images atoms
// These atoms all read/write the single source of truth: imageToVideoAtom.style.

export const selectedEstateIdAtom = atom(
  (get) => get(imageToVideoAtom).style.estateId,
  (_get, set, next: string | null) => {
    set(imageToVideoAtom, (prev) => ({
      ...prev,
      style: {
        ...prev.style,
        estateId: next,

        projectId: next ? null : prev.style.projectId,
      },
    }));
  }
);

export const selectedProjectIdAtom = atom(
  (get) => get(imageToVideoAtom).style.projectId,
  (_get, set, next: string | null) => {
    set(imageToVideoAtom, (prev) => ({
      ...prev,
      style: {
        ...prev.style,
        projectId: next,

        estateId: next ? null : prev.style.estateId,
      },
    }));
  }
);

export const selectedImagesAtom = atom(
  (get) => get(imageToVideoAtom).style.images,
  (_get, set, images: ImageRef[]) => {
    set(imageToVideoAtom, (prev) => {
      const startImageId = images[0]?.id;
      const endImageId = images[images.length - 1]?.id;

      return {
        ...prev,
        style: {
          ...prev.style,
          images,
          startImageId,
          endImageId,
        },
      };
    });
  }
);

export const startImageIdAtom = atom(
  (get) => get(imageToVideoAtom).style.startImageId ?? null,
  (_get, set, id: string | null) => {
    set(imageToVideoAtom, (prev) => ({
      ...prev,
      style: {
        ...prev.style,
        startImageId: id ?? undefined,
      },
    }));
  }
);

export const endImageIdAtom = atom(
  (get) => get(imageToVideoAtom).style.endImageId ?? null,
  (_get, set, id: string | null) => {
    set(imageToVideoAtom, (prev) => ({
      ...prev,
      style: {
        ...prev.style,
        endImageId: id ?? undefined,
      },
    }));
  }
);

export const selectedObjectAtom = atom((get) => {
  const estateId = get(selectedEstateIdAtom);
  const projectId = get(selectedProjectIdAtom);

  return {
    estateId,
    projectId,
  };
});

export const routeParamsHydratedAtom = atom(false);
